import { availableHours, utilization, utilizationBand } from "./capacity.js";

/* The roster, resolved across every contract.
 *
 * A person on three programs is three resource rows, one per contract, with no
 * thread between them. This assembles the thread: it groups those rows into one
 * worker and reports what only the cross-contract view can see, which is who is
 * on many programs at once and whose committed share across all of them is over
 * a hundred percent. That is the key-person question leadership asks and the
 * single page cannot answer.
 *
 * Everything here is pure. It takes the worker rows, the resource rows already
 * loaded per contract, and the holiday list, and returns numbers and groupings.
 * No fetching and no component state, so the same assembly runs on the People
 * page, in a rollup and in a test.
 */

/* Match a resource row to a worker when no worker_id is set yet. People are
   entered by hand on contracts and imported from workbooks, so the same person
   is "Lisa A. Clark" on one and "Lisa Clark" on another. Fold case, drop
   punctuation and a lone middle initial, and collapse spaces, so those two
   land on one key without pretending two different Lisas are the same person. */
export function normalizeName(name) {
  if (!name) return "";
  return String(name)
    .toLowerCase()
    .replace(/[.,]/g, " ")
    .replace(/\b[a-z]\b/g, " ")      // a lone middle initial, now bare of its dot
    .replace(/\s+/g, " ")
    .trim();
}

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

/* How much of this person is committed on one assignment. The resource row
   carries allocation as a percent that was entered by hand; when it is blank
   we cannot invent one, so the assignment counts toward the project list and
   the flags but not toward the combined percentage. */
function allocationOf(resource) {
  const pct = num(resource.allocation_pct);
  return pct === null ? null : Math.max(0, pct);
}

/* One worker, with every contract they sit on resolved from the resource rows.
 *
 * byContract is { contractId: [resourceRow, ...] }, the same shape the
 * portfolio already loads. contracts is the list of contract records, so an
 * assignment can name its program rather than an id. */
export function buildRoster({ workers = [], byContract = {}, contracts = [] } = {}) {
  const contractById = new Map(contracts.map((c) => [c.id, c]));

  /* Index the workers two ways: by id for a linked resource, and by normalized
     name for one that is not linked yet. A worker with a duplicate normalized
     name does not claim the name index, so an ambiguous match falls through to
     unmatched rather than being attached to the wrong person. */
  const byId = new Map();
  const nameCount = new Map();
  for (const w of workers) {
    byId.set(w.id, w);
    const key = normalizeName(w.name);
    if (key) nameCount.set(key, (nameCount.get(key) || 0) + 1);
  }
  const byName = new Map();
  for (const w of workers) {
    const key = normalizeName(w.name);
    if (key && nameCount.get(key) === 1) byName.set(key, w);
  }

  /* Gather assignments onto each worker, and keep the resource rows that match
     no worker so the page can offer to add them rather than hide them. */
  const assignments = new Map();       // workerId -> [assignment]
  const unmatched = new Map();         // normalized name -> { name, contracts:Set }
  const ensure = (id) => { if (!assignments.has(id)) assignments.set(id, []); return assignments.get(id); };

  for (const [contractId, rows] of Object.entries(byContract)) {
    const contract = contractById.get(contractId) || null;
    for (const r of rows || []) {
      if (r.deleted_at) continue;
      let worker = r.worker_id ? byId.get(r.worker_id) : null;
      if (!worker) worker = byName.get(normalizeName(r.title));
      const assignment = {
        contractId,
        contractName: contract?.name || "Unknown contract",
        role: r.role?.trim() || null,
        rate: num(r.rate),
        allocationPct: allocationOf(r),
        hoursPlanned: num(r.hours_planned),
        hoursUsed: num(r.hours_used),
        linked: !!(r.worker_id && byId.get(r.worker_id)),
        resourceId: r.id,
        name: r.title?.trim() || "",
      };
      if (worker) {
        ensure(worker.id).push(assignment);
      } else {
        const key = normalizeName(r.title);
        if (!key) continue;
        if (!unmatched.has(key)) unmatched.set(key, { name: r.title.trim(), contracts: new Set() });
        unmatched.get(key).contracts.add(assignment.contractName);
      }
    }
  }

  const people = workers
    .filter((w) => !w.deleted_at)
    .map((w) => workerSummary(w, assignments.get(w.id) || []));

  const unplaced = [...unmatched.values()]
    .map((u) => ({ name: u.name, contracts: [...u.contracts].sort() }))
    .sort((a, b) => b.contracts.length - a.contracts.length || a.name.localeCompare(b.name));

  return { people: sortRoster(people), unplaced };
}

/* A worker plus the shape of their commitment across programs. */
export function workerSummary(worker, assignments = []) {
  const programs = [...new Set(assignments.map((a) => a.contractName))];
  const allocated = assignments
    .map((a) => a.allocationPct)
    .filter((p) => p !== null);
  const combinedAllocation = allocated.length ? allocated.reduce((s, p) => s + p, 0) : null;
  const rates = assignments.map((a) => a.rate).filter((r) => r !== null);

  /* The two signals the cross-contract view exists to raise. Spread is being
     on several programs at once, which is key-person concentration whatever
     the percentages say. Overallocation is a combined share past a hundred:
     the person is promised to more work than one of them can hold. */
  const spread = programs.length >= 3;
  const overallocated = combinedAllocation !== null && combinedAllocation > 100;

  return {
    worker,
    name: worker.name,
    hireType: worker.hire_type?.trim() || null,
    employment: worker.employment?.trim() || null,
    company: worker.company?.trim() || null,
    workdayManager: worker.workday_manager?.trim() || null,
    location: worker.home_location?.trim() || null,
    status: worker.status || "Active",
    programs,
    programCount: programs.length,
    assignments,
    combinedAllocation,
    rateLow: rates.length ? Math.min(...rates) : null,
    rateHigh: rates.length ? Math.max(...rates) : null,
    spread,
    overallocated,
    keyPerson: spread || overallocated,
  };
}

/* Exception first, the way the whole app reads. The people who need a staffing
   conversation sit at the top: overallocated before merely spread, then by how
   many programs, then by name so the list is stable. */
export function sortRoster(people) {
  const rank = (p) => (p.overallocated ? 2 : p.spread ? 1 : 0);
  return [...people].sort((a, b) =>
    rank(b) - rank(a)
    || b.programCount - a.programCount
    || a.name.localeCompare(b.name));
}

/* The counts a leader reads above the roster: who is on the books, how they
   split by engagement and by base, and how many are stretched. */
export function rosterStats(people = []) {
  const active = people.filter((p) => p.status !== "Inactive");
  const byHireType = tally(active, (p) => p.hireType || "Unspecified");
  const byLocation = tally(active, (p) => p.location || "Location not set");
  const byCompany = tally(active, (p) => p.company || "Unspecified");
  return {
    headcount: active.length,
    keyPeople: active.filter((p) => p.keyPerson).length,
    overallocated: active.filter((p) => p.overallocated).length,
    spread: active.filter((p) => p.spread).length,
    multiProgram: active.filter((p) => p.programCount > 1).length,
    byHireType, byLocation, byCompany,
  };
}

function tally(items, keyOf) {
  const m = new Map();
  for (const it of items) {
    const k = keyOf(it);
    m.set(k, (m.get(k) || 0) + 1);
  }
  return [...m.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

/* A worker's utilization over a period, once the hours are known. Available
   hours come from the capacity math, with this person's own standard week and
   floating holiday folded in, and assigned is the hours they are committed for.
   This is the bridge to the weekly grid that lands next: give it real assigned
   hours and it reads true. */
export function workerUtilization({
  worker, assignedHours = 0, start, end, holidays = [], timeOffHours = 0,
} = {}) {
  const dayHours = worker?.weekly_standard_hours
    ? Number(worker.weekly_standard_hours) / 5
    : undefined;
  /* The personal floating holiday is a day of its own, counted when it falls in
     the period, on top of whatever time off was booked. */
  let off = Number.isFinite(Number(timeOffHours)) ? Number(timeOffHours) : 0;
  const floating = worker?.floating_holiday_date;
  if (floating && start && end && floating >= start && floating <= end) {
    off += dayHours || 8;
  }
  const available = availableHours({ start, end, holidays, timeOffHours: off, dayHours });
  const pct = utilization({ assignedHours, available });
  return { available, pct, band: utilizationBand(pct) };
}
